import React, { useState, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { ChevronDown } from "lucide-react";

export default function AutocompleteInput({ 
  value, 
  onChange, 
  suggestions = [], 
  placeholder = "",
  className = "",
  inputClassName = ""
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter suggestions based on input
  useEffect(() => {
    if (value && value.length > 0) {
      const searchTerms = value.toLowerCase().split(' ').filter(t => t.length > 0);
      const filtered = suggestions.filter(suggestion => {
        const suggestionLower = suggestion.toLowerCase();
        return searchTerms.every(term => suggestionLower.includes(term));
      }).slice(0, 15); // Limit to 15 results
      setFilteredSuggestions(filtered);
    } else {
      // Show first 15 suggestions when empty
      setFilteredSuggestions(suggestions.slice(0, 15));
    }
    setHighlightedIndex(-1);
  }, [value, suggestions]);

  // Handle click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  const handleInputChange = (e) => {
    onChange(e.target.value);
    setIsOpen(true);
  };

  const handleSelect = (suggestion) => {
    onChange(suggestion);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
          handleSelect(filteredSuggestions[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "Tab":
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`pr-8 ${inputClassName}`}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white transition-colors"
          tabIndex={-1}
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>
      
      {isOpen && filteredSuggestions.length > 0 && (
        <div 
          ref={listRef}
          className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-zinc-900 border border-zinc-700 rounded-md shadow-lg"
        >
          {filteredSuggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                index === highlightedIndex 
                  ? 'bg-primary text-white' 
                  : 'text-zinc-300 hover:bg-zinc-800'
              }`}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
      
      {isOpen && value && filteredSuggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-md shadow-lg">
          <div className="px-3 py-2 text-sm text-zinc-500 italic">
            Nessun risultato - inserisci manualmente
          </div>
        </div>
      )}
    </div>
  );
}
